import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  MessageSquare, Plus, Edit, Trash2, Eye, Send, 
  CheckCircle, Clock, XCircle, AlertCircle, RefreshCw,
  Smartphone, Calendar, DollarSign, Truck, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Event trigger icons and colors
const EVENT_CONFIG = {
  LEAD_CREATED: { icon: Package, color: 'bg-blue-100 text-blue-700', label: 'Lead Created' },
  PICKUP_SCHEDULED: { icon: Calendar, color: 'bg-purple-100 text-purple-700', label: 'Pickup Scheduled' },
  PICKUP_REMINDER: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pickup Reminder' },
  PAYMENT_COMPLETED: { icon: DollarSign, color: 'bg-green-100 text-green-700', label: 'Payment Completed' },
  DEVICE_RECEIVED: { icon: Truck, color: 'bg-teal-100 text-teal-700', label: 'Device Received' },
  PRICE_REVISION: { icon: AlertCircle, color: 'bg-orange-100 text-orange-700', label: 'Price Revision' }
};

// Meta status badges
const STATUS_BADGES = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Edit },
  PENDING: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  APPROVED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  REJECTED: { color: 'bg-red-100 text-red-700', icon: XCircle }
};

const BuybackWhatsAppTemplates = ({ user, onLogout }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    event_trigger: 'LEAD_CREATED',
    category: 'UTILITY',
    language: 'en',
    header_type: 'TEXT',
    header_text: '',
    body_text: '',
    footer_text: '',
    variables: [],
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchNotificationLogs();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/buyback/notifications/templates`);
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/buyback/notifications/logs?limit=50`);
      setNotificationLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/api/buyback/notifications/templates/${editingTemplate.template_id}`, formData);
      } else {
        await axios.post(`${API}/api/buyback/notifications/templates`, formData);
      }
      fetchTemplates();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/api/buyback/notifications/templates/${templateId}`);
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      event_trigger: template.event_trigger,
      category: template.category,
      language: template.language,
      header_type: template.header_type || 'TEXT',
      header_text: template.header_text || '',
      body_text: template.body_text,
      footer_text: template.footer_text || '',
      variables: template.variables || [],
      is_active: template.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      event_trigger: 'LEAD_CREATED',
      category: 'UTILITY',
      language: 'en',
      header_type: 'TEXT',
      header_text: '',
      body_text: '',
      footer_text: '',
      variables: [],
      is_active: true
    });
  };

  const addVariable = () => {
    const varName = prompt('Enter variable name (e.g., customer_name, lead_id, device):');
    if (varName && !formData.variables.includes(varName)) {
      setFormData(prev => ({
        ...prev,
        variables: [...prev.variables, varName]
      }));
    }
  };

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPreview = (template) => {
    // Replace placeholders with sample data
    let preview = template.body_text;
    const sampleData = {
      customer_name: 'Rahul Sharma',
      lead_id: 'BBL-260107-00001',
      device: 'iPhone 15 Pro Max',
      quoted_price: '85,000',
      final_price: '82,000',
      pickup_date: '10 Jan 2026',
      pickup_time: '10:00 AM - 12:00 PM',
      pickup_address: '123 Main Street, Mumbai',
      engineer_name: 'Amit Kumar',
      engineer_phone: '9876543210',
      po_number: 'PO-BB-260107-00001',
      payment_mode: 'UPI'
    };
    
    template.variables?.forEach((varName, index) => {
      preview = preview.replace(`{{${index + 1}}}`, sampleData[varName] || `[${varName}]`);
    });
    
    return preview;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <MessageSquare className="w-7 h-7 text-green-600" />
              Buyback WhatsApp Templates
            </h1>
            <p className="text-gray-500">Manage automated WhatsApp notifications for buyback leads</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'templates' 
                ? 'bg-[#2b3380] text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Templates ({templates.length})
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'logs' 
                ? 'bg-[#2b3380] text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Notification Logs ({notificationLogs.length})
          </button>
        </div>

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2b3380]"></div>
              </div>
            ) : templates.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No templates found. Create your first template!
              </div>
            ) : (
              templates.map(template => {
                const eventConfig = EVENT_CONFIG[template.event_trigger] || EVENT_CONFIG.LEAD_CREATED;
                const EventIcon = eventConfig.icon;
                const statusConfig = STATUS_BADGES[template.meta_status] || STATUS_BADGES.DRAFT;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div key={template.template_id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className={`p-4 ${eventConfig.color}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <EventIcon className="w-5 h-5" />
                          <span className="font-semibold">{eventConfig.label}</span>
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {template.meta_status}
                        </div>
                      </div>
                    </div>
                    
                    {/* Body */}
                    <div className="p-4">
                      <h3 className="font-medium text-gray-800 mb-1">{template.name}</h3>
                      {template.header_text && (
                        <p className="text-sm font-semibold text-gray-700 mb-2">{template.header_text}</p>
                      )}
                      <p className="text-sm text-gray-600 line-clamp-3">{template.body_text}</p>
                      
                      {/* Variables */}
                      {template.variables?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {template.variables.map((v, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              {`{{${i + 1}}} = ${v}`}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Status Toggle */}
                      <div className="mt-3 flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full ${template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-400">{template.language.toUpperCase()}</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
                      <button
                        onClick={() => setShowPreview(template)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.template_id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">Recent Notifications</h2>
              <button onClick={fetchNotificationLogs} className="p-2 text-gray-500 hover:text-gray-700">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {notificationLogs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No notification logs yet</div>
              ) : (
                notificationLogs.map((log, index) => (
                  <div key={index} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${log.status === 'sent' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div>
                          <p className="font-medium text-gray-800">{log.template_name}</p>
                          <p className="text-sm text-gray-500">
                            Lead: {log.lead_id} • To: {log.to}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {log.status}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(log.sent_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name (Meta)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="zobox_buyback_lead_created"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Use lowercase with underscores, matching Meta template name</p>
                </div>
                
                {/* Event Trigger */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Trigger</label>
                  <select
                    value={formData.event_trigger}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_trigger: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {Object.entries(EVENT_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Category & Language */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="UTILITY">Utility</option>
                      <option value="MARKETING">Marketing</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="en_US">English (US)</option>
                    </select>
                  </div>
                </div>
                
                {/* Header */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Header Text (Optional)</label>
                  <input
                    type="text"
                    value={formData.header_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, header_text: e.target.value }))}
                    placeholder="Sell Request Received ✅"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                {/* Body Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body Text *</label>
                  <textarea
                    value={formData.body_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, body_text: e.target.value }))}
                    placeholder="Hi {{1}}, Your sell request for {{2}} has been received..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Use {'{{1}}'}, {'{{2}}'}, etc. for variables</p>
                </div>
                
                {/* Footer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text (Optional)</label>
                  <input
                    type="text"
                    value={formData.footer_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, footer_text: e.target.value }))}
                    placeholder="Zobox - India's Trusted Refurbished Phone Platform"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                {/* Variables */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variables (in order)</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.variables.map((v, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg">
                        {`{{${i + 1}}} = ${v}`}
                        <button type="button" onClick={() => removeVariable(i)} className="hover:text-red-600">×</button>
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Variable
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Available: customer_name, lead_id, device, quoted_price, final_price, pickup_date, pickup_time, pickup_address, engineer_name, engineer_phone, po_number, payment_mode
                  </p>
                </div>
                
                {/* Active Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Template is active</label>
                </div>
                
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">Message Preview</h2>
                <button onClick={() => setShowPreview(null)} className="p-1 hover:bg-gray-100 rounded">×</button>
              </div>
              
              {/* WhatsApp-like preview */}
              <div className="p-4 bg-[#e5ddd5]">
                <div className="bg-white rounded-lg shadow p-3 max-w-[85%] ml-auto">
                  {showPreview.header_text && (
                    <p className="font-semibold text-gray-800 mb-1">{showPreview.header_text}</p>
                  )}
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {renderPreview(showPreview)}
                  </p>
                  {showPreview.footer_text && (
                    <p className="text-xs text-gray-500 mt-2 border-t pt-2">{showPreview.footer_text}</p>
                  )}
                  <p className="text-xs text-gray-400 text-right mt-1">10:30 AM ✓✓</p>
                </div>
              </div>
              
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={() => setShowPreview(null)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BuybackWhatsAppTemplates;
